import React, { useEffect, useState } from "react";
import { hasRole } from "../../lib/roles";
import { useNavigate } from "react-router-dom";
import StorefrontLayout from "../../components/storefront/StorefrontLayout";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { fmtINR } from "../../lib/constants";
import { toast } from "sonner";
import { CheckCircle, Loader2 } from "lucide-react";
import { assistGet, assistEnd } from "../../lib/assist";

export default function Checkout() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [prices, setPrices] = useState({});
  // When a staff member is assisting, the checkout must show the CUSTOMER's
  // details (name / phone / email), never the salesperson's.
  const assistInit = assistGet();
  const [address, setAddress] = useState({
    name: assistInit?.name || user?.name || "",
    phone: assistInit?.phone || user?.phone || "",
    email: assistInit ? "" : (user?.email || ""),
    line1: "", city: "", state: "Karnataka", pincode: "",
  });
  const [delivery_mode, setMode] = useState("delivery");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [settings, setSettings] = useState({ delivery_fee: 250, free_delivery_above: 25000, counter_payment_enabled: true });
  const [paymentMethod, setPaymentMethod] = useState("online");

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    // Assisting? Pull the customer's saved details so checkout shows THEM.
    const assist = assistGet();
    if (assist?.phone) {
      api.get(`/api/crm/customers/by-phone/${encodeURIComponent(assist.phone)}`).then(({ data }) => {
        const c = data?.customer;
        if (c) setAddress((a) => ({
          ...a,
          name: c.name || a.name,
          phone: c.phone || a.phone,
          email: c.email || a.email,
          line1: a.line1 || c.address || "",
        }));
      }).catch(() => {});
    }
    api.get("/api/cart").then(async ({ data }) => {
      setItems(data.items || []);
      const pm = {};
      for (const it of data.items || []) {
        // Custom orders are priced on their own weight×purity estimate; regular
        // lines use the live product price.
        if (it.is_customised && it.custom_estimate) {
          pm[it.product.id] = it.custom_estimate * (it.qty || 1);
          continue;
        }
        try { const { data: pd } = await api.get(`/api/products/${it.product.id}/price`); pm[it.product.id] = pd.price * (it.qty || 1); } catch {}
      }
      setPrices(pm);
    });
    api.get("/api/site-settings").then((r) => r.data && setSettings((s) => ({ ...s, ...r.data })));
  }, [user, nav]);

  const applyCoupon = async () => {
    if (!couponCode) return;
    const { data } = await api.get(`/api/coupon/validate/${couponCode.toUpperCase()}`);
    if (data.valid) { setCoupon(data.coupon); toast.success(`Coupon applied: ${data.coupon.code}`); }
    else { toast.error("Invalid coupon"); }
  };

  const subtotal = Object.values(prices).reduce((a, b) => a + b, 0);
  const discount = coupon ? (coupon.discount_pct ? subtotal * (coupon.discount_pct / 100) : coupon.discount_amount) : 0;
  const fee = delivery_mode === "pickup" ? 0 : (subtotal > settings.free_delivery_above ? 0 : settings.delivery_fee);
  const total = Math.max(0, subtotal - discount + fee);
  // 20% of any customised line is deferred to delivery.
  const customBalance = Math.round(
    (items || []).reduce((a, it) => a + (it.is_customised ? (prices[it.product?.id] || 0) : 0), 0) * 0.2);

  const submit = async () => {
    if (!address.name || !address.phone || (delivery_mode === "delivery" && !address.pincode)) {
      toast.error("Please complete address"); return;
    }
    setBusy(true);
    try {
      // Assist mode: staff checking out on behalf of a customer — link the order to them
      const assist = assistGet();
      const payload = {
        items: items.map((it) => ({
          product_id: it.product.id,
          qty: it.qty,
          is_customised: !!it.is_customised,
          customisation_note: it.customisation_note || "",
          customisation_images: it.customisation_images || [],
          custom_weight: it.custom_weight ?? null,
          custom_purity: it.custom_purity || "",
        })),
        address, delivery_mode,
        notes: paymentMethod === "counter" ? "COUNTER PAYMENT — collect at store" : (coupon ? `Coupon: ${coupon.code}` : ""),
        salesperson_id: assist && user && hasRole(user, ["salesperson", "admin", "major_admin", "super_admin"]) ? user.id : "",
        assisted_phone: assist?.phone || "",
      };
      const { data } = await api.post("/api/checkout", payload);
      if (paymentMethod === "counter") {
        // Mark order as pending-counter; verify with mock signature flag
        await api.post("/api/checkout/verify", {
          order_id: data.order.id,
          razorpay_order_id: data.razorpay.order_id,
          razorpay_payment_id: `counter_${Date.now()}`,
          razorpay_signature: "counter-pay",
        });
        setSuccess({ ...data.order, payment_method: "counter" }); toast.success("Order booked — pay at store"); return;
      }
      if (data.razorpay.mock) {
        const v = await api.post("/api/checkout/verify", {
          order_id: data.order.id,
          razorpay_order_id: data.razorpay.order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: "mock-sig",
        });
        if (v.data.verified) { setSuccess(data.order); toast.success("Payment successful (test mode)"); }
        else toast.error("Verification failed");
      } else {
        const rzp = new window.Razorpay({
          key: data.razorpay.key_id, amount: data.razorpay.amount, currency: data.razorpay.currency,
          order_id: data.razorpay.order_id, name: "Swayambhu Gold",
          prefill: { name: address.name || user.name, email: address.email || user.email, contact: address.phone },
          handler: async (resp) => {
            const v = await api.post("/api/checkout/verify", {
              order_id: data.order.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            if (v.data.verified) { setSuccess(data.order); toast.success("Payment successful"); }
            else toast.error("Verification failed");
          },
        });
        rzp.open();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally { setBusy(false); }
  };

  if (success) {
    return (
      <StorefrontLayout>
        <div className="px-6 lg:px-12 py-24 text-center" data-testid="checkout-success">
          <CheckCircle size={48} className="mx-auto text-gold mb-4"/>
          <h1 className="font-display text-5xl text-maroon">Order Confirmed</h1>
          <p className="mt-2 text-ash">Order #{success.id.slice(0,8)} — {fmtINR(success.total)}</p>
          <p className="mt-1 text-xs text-ash">A GST invoice will be sent to your registered email.</p>
          <button onClick={() => nav("/")} className="btn-maroon mt-8 inline-flex">Continue Shopping</button>
        </div>
      </StorefrontLayout>
    );
  }

  return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-10">
        <h1 className="font-display text-4xl text-maroon">Checkout</h1>
        <div className="royal-divider my-6"/>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-line p-6">
              <div className="eyebrow mb-4">Delivery method</div>
              <div className="grid grid-cols-2 gap-3">
                {["delivery", "pickup"].map((m) => (
                  <button key={m} onClick={() => setMode(m)} className={`p-4 border ${delivery_mode === m ? "border-maroon bg-pearl" : "border-line"} text-left`} data-testid={`delivery-mode-${m}`}>
                    <div className="font-medium capitalize">{m === "delivery" ? "Home Delivery" : "Store Pickup"}</div>
                    <div className="text-xs text-ash mt-1">{m === "delivery" ? "Free over ₹25,000" : "Pickup at any showroom"}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white border border-line p-6 space-y-3">
              <div className="eyebrow mb-2">{delivery_mode === "pickup" ? "Pickup contact" : "Shipping address"}</div>
              {assistGet()?.phone && <div className="text-[11px] text-maroon bg-gold/10 border border-gold px-3 py-2 rounded" data-testid="assist-checkout-note">Assisting customer — these are the customer's details, not yours.</div>}
              <input data-testid="addr-name" placeholder="Full name" className="w-full border border-line px-3 py-2 bg-pearl" value={address.name} onChange={(e) => setAddress({ ...address, name: e.target.value })} />
              <input data-testid="addr-phone" placeholder="Phone" maxLength={10} className="w-full border border-line px-3 py-2 bg-pearl" value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} />
              <input data-testid="addr-email" type="email" placeholder="Email" className="w-full border border-line px-3 py-2 bg-pearl" value={address.email} onChange={(e) => setAddress({ ...address, email: e.target.value.toLowerCase() })} />
              {delivery_mode === "delivery" && (
                <>
                  <input data-testid="addr-line1" placeholder="Address line" className="w-full border border-line px-3 py-2 bg-pearl" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} />
                  <div className="grid grid-cols-3 gap-2">
                    <input data-testid="addr-city" placeholder="City" className="border border-line px-3 py-2 bg-pearl" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} />
                    <input data-testid="addr-state" placeholder="State" className="border border-line px-3 py-2 bg-pearl" value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} />
                    <input data-testid="addr-pincode" placeholder="PIN" className="border border-line px-3 py-2 bg-pearl" value={address.pincode} onChange={(e) => setAddress({ ...address, pincode: e.target.value })} />
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="bg-white border border-line p-6 h-fit sticky top-32">
            <div className="eyebrow mb-4">Summary</div>
            {items.map((it) => (
              <div key={it.product.id} className="flex justify-between text-sm mb-2">
                <span className="truncate mr-2">{it.product.name}</span>
                <span>{fmtINR(prices[it.product.id] || 0)}</span>
              </div>
            ))}
            <div className="royal-divider my-3"/>
            <div className="flex gap-2 mb-3">
              <input placeholder="Coupon code" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} className="flex-1 border border-line px-3 py-2 bg-pearl text-sm" data-testid="coupon-input"/>
              <button onClick={applyCoupon} className="btn-outline text-xs" data-testid="apply-coupon">Apply</button>
            </div>
            {coupon && <div className="text-xs text-green-700 mb-2">Coupon {coupon.code} applied · saves {fmtINR(discount)}</div>}
            <div className="flex justify-between text-sm mb-1"><span>Subtotal</span><span>{fmtINR(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-sm mb-1 text-green-700"><span>Discount</span><span>-{fmtINR(discount)}</span></div>}
            <div className="flex justify-between text-sm mb-1"><span>Delivery</span><span>{fee ? fmtINR(fee) : "Free"}</span></div>
            <div className="flex justify-between font-display text-xl text-maroon mt-2"><span>Total</span><span>{fmtINR(total)}</span></div>
            {customBalance > 0 && (
              <div className="border-t border-line mt-2 pt-2 text-xs" data-testid="checkout-advance-note">
                <div className="flex justify-between text-maroon font-medium">
                  <span>Payable now (80% on customised)</span><span>{fmtINR(total - customBalance)}</span>
                </div>
                <div className="flex justify-between text-ash mt-0.5">
                  <span>Balance on delivery</span><span>{fmtINR(customBalance)}</span>
                </div>
              </div>
            )}
            <div className="mt-4">
              <div className="eyebrow text-[10px] mb-2">Payment method</div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" checked={paymentMethod === "online"} onChange={() => setPaymentMethod("online")} data-testid="pay-online"/> Online (Razorpay)</label>
                {settings.counter_payment_enabled && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" checked={paymentMethod === "counter"} onChange={() => setPaymentMethod("counter")} data-testid="pay-counter"/> Pay at store counter</label>
                )}
              </div>
            </div>
            <button onClick={submit} disabled={busy || items.length === 0} className="btn-maroon w-full justify-center mt-5" data-testid="pay-now-btn">
              {busy ? <><Loader2 size={14} className="animate-spin"/> Processing</> : paymentMethod === "counter" ? "Confirm — Pay at Store" : "Pay Now"}
            </button>
            <p className="text-[10px] text-ash mt-3 text-center">Secured by Razorpay · Mock mode for preview.</p>
          </div>
        </div>
      </div>
    </StorefrontLayout>
  );
}
